import React from "react";
import { useNavigate } from "react-router-dom";
import {
	Paper,
	Typography,
	Button,
	Box,
	Alert,
	Chip,
	Card,
	CardContent,
} from "@mui/material";
import {
	Schedule,
	CalendarMonth,
	AccessTime,
} from "@mui/icons-material";

import { useBooth } from "../../contexts/BoothContext";

export default function ReservationBookingSection() {
	const navigate = useNavigate();
	const {
		booth,
		isBoothAvailable,
		boothDisplayName,
	} = useBooth();

	const handleBookReservation = () => {
		// Navigate to the existing time scheduler with booth context
		// This follows the existing reservation flow but with pre-selected booth
		navigate(`/${booth.location._id || booth.location}/reservation/time-scheduler`, {
			state: {
				boothId: booth.boothId, // Numeric ID for internal logic
				boothObjectId: booth._id, // MongoDB ObjectId for reference
				preSelectedBooth: booth, // Full booth data
				locationId: booth.location._id || booth.location,
				saltoInfo: booth.saltoInfo,
				fromBoothPage: true, // Flag to indicate we came from booth page
			},
		});
	};

	if (!isBoothAvailable) {
		const isOccupied = booth?.isOccupied;
		const status = booth?.status;
		
		return (
			<Paper elevation={2} sx={{ p: 3, mt: 2 }}>
				<Alert 
					severity={isOccupied ? "error" : "warning"}
					sx={{ mb: 2 }}
				>
					<Typography variant="h6" sx={{ mb: 1 }}>
						{isOccupied ? "Booth Currently Occupied" : "Booth Unavailable"}
					</Typography>
					<Typography variant="body2">
						{isOccupied 
							? "Someone is currently using this booth. You cannot make a reservation while it's occupied."
							: `This booth is currently ${status || "unavailable"}. Please try another booth or contact support.`
						}
					</Typography>
				</Alert>
				
				{isOccupied && (
					<Box sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
						<Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
							What you can do:
						</Typography>
						<Typography component="ul" variant="body2" sx={{ pl: 2, m: 0 }}>
							<Typography component="li">Look for another available booth nearby</Typography>
							<Typography component="li">Wait for the current session to end, then try again</Typography>
							<Typography component="li">Contact support if you need assistance</Typography>
						</Typography>
					</Box>
				)}
			</Paper>
		);
	}

	return (
		<Paper elevation={2} sx={{ p: 3, mt: 2 }}>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
				<Schedule color="primary" />
				<Typography variant="h6">Schedule a Reservation</Typography>
				<Chip 
					label="Reservation" 
					color="primary" 
					size="small"
				/>
			</Box>

			<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
				Book this booth for a specific time slot. Perfect for planned meetings and work sessions.
			</Typography>

			{/* Features */}
			<Card variant="outlined" sx={{ mb: 3 }}>
				<CardContent>
					<Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
						Reservation Benefits
					</Typography>
					<Box component="ul" sx={{ pl: 2, m: 0 }}>
						<Typography component="li" variant="body2" sx={{ mb: 1 }}>
							<strong>Guaranteed availability</strong> - Your booth is reserved for your chosen time
						</Typography>
						<Typography component="li" variant="body2" sx={{ mb: 1 }}>
							<strong>Flexible scheduling</strong> - Book up to 7 days in advance
						</Typography>
						<Typography component="li" variant="body2" sx={{ mb: 1 }}>
							<strong>Multiple durations</strong> - Choose from 15, 30, or 60-minute slots
						</Typography>
						<Typography component="li" variant="body2">
							<strong>Easy modification</strong> - Change or cancel your reservation if needed
						</Typography>
					</Box>
				</CardContent>
			</Card>

			{/* How it works */}
			<Box sx={{ mb: 3 }}>
				<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
					How reservations work:
				</Typography>
				<Box component="ol" sx={{ pl: 2, m: 0 }}>
					<Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
						Select your preferred date and time slot
					</Typography>
					<Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
						Choose session duration (15, 30, or 60 minutes)
					</Typography>
					<Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
						Complete registration and confirm your booking
					</Typography>
					<Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
						Arrive at your scheduled time and unlock the booth
					</Typography>
					<Typography component="li" variant="body2">
						Enjoy your productive work session!
					</Typography>
				</Box>
			</Box>

			{/* Action Button */}
			<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
				<Button
					variant="contained"
					size="large"
					startIcon={<CalendarMonth />}
					onClick={handleBookReservation}
					sx={{ 
						flexGrow: 1,
						py: 1.5,
						fontSize: "1.1rem",
					}}
				>
					Select Time & Book
				</Button>
			</Box>

			{/* Additional Info */}
			<Alert severity="info" sx={{ mt: 2 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<AccessTime fontSize="small" />
					<Typography variant="body2">
						You can arrive up to 15 minutes early for your reservation. 
						The booth will unlock automatically at your scheduled time.
					</Typography>
				</Box>
			</Alert>
		</Paper>
	);
}